import { sinkReason } from '@halvard/board3d';
import { ice, squareName } from '@halvard/rules';
import { t } from '../../i18n';

const name = (sq: ice.Square) =>
  sq.r >= 0 && sq.r <= 8 && sq.c >= 0 && sq.c <= 8 ? squareName(sq.r, sq.c) : t().log.offTheIce;

export function warriorName(w: Pick<ice.Warrior, 'side' | 'type'>): string {
  return t().warriorName(t().sides[w.side], t().warriors[w.type]);
}

/** "3 against 2 (2 force + 1 shield against 1 force + 1 shield)". */
export function shoveSums(
  shover: ice.Warrior,
  target: ice.Warrior,
  strength: number,
  wall: number,
): string {
  const { force, sums } = t().log;
  const mine = ice.FORCE[shover.type];
  const theirs = ice.FORCE[target.type];
  return sums(strength, wall, force(mine, strength - mine), force(theirs, wall - theirs));
}

/** Sentences for the move log and screen readers, from the state before the action. */
export function describeIceEvents(before: ice.IceState, events: readonly ice.IceEvent[]): string[] {
  const s = t().log;
  const find = (id: string) => before.warriors.find((w) => w.id === id);
  const lines: string[] = [];
  const cracked: string[] = [];
  for (const event of events) {
    switch (event.kind) {
      case 'placed':
        lines.push(s.placed(warriorName(event.warrior), name(event.warrior)));
        break;
      case 'moved': {
        const w = find(event.id);
        const from = event.path[0];
        const to = event.path.at(-1);
        if (w && from && to) lines.push(s.moved(warriorName(w), name(from), name(to)));
        break;
      }
      case 'shoved': {
        const w = find(event.id);
        const first = event.slides.at(-1);
        const target = first && find(first.id);
        if (!w || !target || !first) break;
        lines.push(
          s.shoved(
            warriorName(w),
            warriorName(target),
            name(first.from),
            shoveSums(w, target, event.strength, event.wall),
          ),
        );
        for (const slide of event.slides) {
          const slid = find(slide.id);
          const reason = sinkReason(before, slide);
          if (slid && reason) lines.push(s.sunk(warriorName(slid), reason));
        }
        break;
      }
      case 'passed':
        lines.push(s.passes(t().sides[event.side]));
        break;
      case 'glory':
        if (event.gained > 0) lines.push(s.glory(event.gained, event.total));
        break;
      case 'thawed': {
        const sunk = event.sunk ? find(event.sunk) : undefined;
        lines.push(
          event.height === ice.HOLE
            ? s.givesWay(name(event), sunk ? warriorName(sunk) : null)
            : s.thins(name(event), event.height),
        );
        break;
      }
      case 'cracked':
        cracked.push(name(event));
        break;
      case 'over':
        lines.push(outcomeText(event.winner, event.reason).title);
        break;
    }
  }
  if (cracked.length > 0) lines.push(s.groans(cracked));
  return lines;
}

export function outcomeText(
  winner: ice.Side,
  reason: ice.IceWinReason,
): { title: string; detail: string } {
  const { sides, outcome } = t();
  const loser = sides[winner === 'brand' ? 'rime' : 'brand'];
  const title = outcome.wins(sides[winner]);
  switch (reason) {
    case 'glory':
      return { title, detail: outcome.glory };
    case 'held':
      return { title, detail: outcome.held };
    case 'sunk':
      return { title, detail: outcome.sunk(loser) };
    case 'resign':
      return { title, detail: outcome.resign(loser) };
  }
}
